import * as React from "react";

import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import { cn } from "../../lib/utils";

import { DataTableRowKebab } from "./data-table-row-kebab";
import { useDataTableDensity, useDataTableRowKebab } from "./data-table-context";
import type { DataTableTextBrightness } from "./data-table-types";
import {
  CELL_ALIGNMENT_CLASS,
  DENSITY_CONFIG,
  ROW_ACTIONS_END_INSET,
  ROW_FLOATING_CHROME_CLASS,
  type DataTableCellAlignment,
  isOwnPress,
  pressArmedClosest,
} from "./data-table-utils";
import type { Omittable } from "../../lib/props";

type PaddingVariant = "default" | "withLeft";

export type DataTableCellContentProps = Omittable<{
  align?: DataTableCellAlignment;
  padding?: PaddingVariant;
  className?: string;
  children?: React.ReactNode;
  "data-testid"?: string;
}>;

export type DataTableHoverActionsCellProps = Omit<
  DataTableCellContentProps,
  "children"
> &
  Omittable<{
    children: React.ReactNode;
    actions: React.ReactNode;
    actionsVisibility?: "hover" | "always";
    onClick?: () => void;
    "aria-label"?: string;
  }>;

export type DataTableCellContentAlignment = DataTableCellAlignment;

/**
 * Low-level cell content wrapper providing density-aware padding and alignment.
 */
function DataTableCellContent({
  align = "start",
  padding = "default",
  className,
  children,
  ...props
}: DataTableCellContentProps) {
  const density = useDataTableDensity();
  const densityConfig = DENSITY_CONFIG[density];
  const paddingClass =
    padding === "withLeft"
      ? densityConfig.cellPaddingWithLeft
      : densityConfig.cellPadding;

  return (
    <Box
      fill
      shrink
      direction="row"
      align="center"
      className={cn(
        "h-full",
        CELL_ALIGNMENT_CLASS[align],
        paddingClass,
        className,
      )}
      {...props}
    >
      {children}
    </Box>
  );
}

/**
 * Cell with content and an action cluster that reveals while the ROW is
 * hovered — not the cell: this is the row's identity cell, and its actions are
 * the row's, so a pointer anywhere along the row lights them (the reference
 * behaviour). The row is the `group/row` scope DataTable stamps on every
 * body row. Other cells keep whatever per-cell hover they own; only this
 * cluster is row-scoped. It snaps in with no transition, stays while a popup
 * anchored anywhere in the row is open, and on touch it shows on the armed
 * (tapped) row. When `onClick` is provided, the entire cell becomes
 * clickable.
 *
 * A hover-revealed cluster FLOATS over the content's trailing edge
 * (ROW_FLOATING_CHROME_CLASS) and reserves no width, so at rest the content
 * runs the whole cell. When DataTable hosts the row's kebab in this cell, the
 * cluster ADOPTS it as its last item (DataTableRowKebabHost offers it through
 * context): the cell's actions and the kebab are ONE float with ONE backing
 * ramp leading all of them, where a second float's ramp would land on the
 * first's last button. An always-visible cluster stays in flow beside the
 * content — it is never hidden, so there is nothing to give the content back
 * — and adopts nothing; the kebab floats standalone over it.
 *
 * A press a control inside the cell claims — an action button, the kebab, a
 * pick in its portaled menu — is that control's, not the cell's: `onClick`
 * fires only for the cell's own press (isOwnPress), so an action never also
 * opens the row.
 */
function DataTableHoverActionsCell({
  align = "start",
  padding = "default",
  actions,
  actionsVisibility = "hover",
  onClick,
  "aria-label": ariaLabel,
  className,
  children,
}: DataTableHoverActionsCellProps) {
  const hoverControlled = actionsVisibility === "hover";
  const hasActions = React.Children.count(actions) > 0;
  const kebab = useDataTableRowKebab();
  const adoptsKebab = hoverControlled && kebab != null;
  const adopt = kebab?.adopt;
  // Layout effect: the host drops its standalone kebab before first paint.
  React.useLayoutEffect(() => {
    if (!adoptsKebab || !adopt) return;
    return adopt();
  }, [adoptsKebab, adopt]);

  // The whole cell opens the row, but only the CONTENT is the named button:
  // the action cluster (and the kebab it adopts) is its sibling, never its
  // descendant — a button that holds buttons is `nested-interactive`, and a
  // screen reader may not announce the inner controls. The content's own
  // press and a press on the cell's padding each fire once: `isOwnPress`
  // gives a press inside the content to the content, not to the cell.
  const handlePress = onClick
    ? (event: React.MouseEvent<HTMLElement>) => {
        if (!isOwnPress(event.currentTarget, event.target)) return;
        // The click a HELD press produced arming the row is spent on the
        // reveal: the name does not open. A tap opens.
        if (pressArmedClosest(event.currentTarget)) return;
        onClick();
      }
    : undefined;

  // The named button takes Tab focus and opens on Enter or Space, as a
  // native button would: a role alone gives a keyboard user nothing to press.
  const handleKeyDown = onClick
    ? (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onClick();
      }
    : undefined;

  return (
    <Box
      direction="column"
      fill="cell"
      // A clickable name cell keeps its resting ink on hover — the cursor and
      // the row hover are the affordance. No accent token is solved as ink
      // against the page (`--primary` is a fill; `--brand` is never neutral),
      // so a hover recolor cannot be both accessible under every seed and
      // neutral under the default theme.
      onClick={handlePress}
      cursor={onClick ? "pointer" : undefined}
      // A clickable cell owns its clicks — a row-level `onRowClick` must not
      // fire a second time beneath it (see ROW_CLICK_CLAIMED_SELECTOR).
      data-row-click-stop={onClick ? "" : undefined}
    >
      <DataTableCellContent
        align={align}
        padding={padding}
        className={cn("gap-2", hoverControlled && "relative", className)}
      >
        <Box
          minWidth={0}
          fill="cell"
          shrink
          justify="center"
          onClick={handlePress}
          onKeyDown={handleKeyDown}
          role={onClick ? "button" : undefined}
          tabIndex={onClick ? 0 : undefined}
          aria-label={ariaLabel}
        >
          {children}
        </Box>
        {hasActions || adoptsKebab ? (
          <Box
            shrink={false}
            direction="row"
            align="center"
            gap="xs"
            // Row chrome: hover on a mouse, the armed (tapped) row on touch.
            hoverReveal={hoverControlled ? "row" : undefined}
            right={hoverControlled ? ROW_ACTIONS_END_INSET : undefined}
            className={cn(hoverControlled && ROW_FLOATING_CHROME_CLASS)}
          >
            {actions}
            {adoptsKebab ? <DataTableRowKebab actions={kebab.actions} /> : null}
          </Box>
        ) : null}
      </DataTableCellContent>
    </Box>
  );
}

/**
 * Default cell renderer for simple text values.
 * Wraps cell content in a Text component with appropriate styling.
 */
function DataTableCell({
  children,
  truncate = true,
  align = "start",
  padding = "default",
  textBrightness = "high",
}: Omittable<{
  children: React.ReactNode;
  truncate?: boolean;
  align?: DataTableCellAlignment;
  padding?: PaddingVariant;
  textBrightness?: DataTableTextBrightness;
}>) {
  const alignmentClass =
    align === "end"
      ? "text-right tabular-nums"
      : align === "center"
        ? "text-center"
        : "text-left";

  return (
    <DataTableCellContent align={align} padding={padding}>
      {/* size={null} suppresses Text's default responsive size classes so the
          density text-size class set on TableRow inherits down. */}
      <Text
        size={null}
        variant={textBrightness === "subtle" ? "muted" : undefined}
        truncate={truncate}
        className={cn(truncate && "flex-1", "min-w-0", alignmentClass)}
      >
        {children}
      </Text>
    </DataTableCellContent>
  );
}

export { DataTableCellContent, DataTableHoverActionsCell, DataTableCell };
